import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { Anchor, Badge, Box, Group, Select, Switch, getDefaultZIndex, rem } from '@mantine/core';
import { api } from '../lib/api';
import type { ApiData } from '../lib/api';
import { useAuth } from '../contexts/auth';

type DevUser = ApiData<typeof api.dev.users.get>[number];

const DEV_SWITCHER_HEIGHT = 44;
const DEV_SWITCHER_HEIGHT_VAR = '--cd-dev-switcher-height';

export const DEV_SWITCHER_OFFSET = `var(${DEV_SWITCHER_HEIGHT_VAR}, 0px)`;

function displayName(user: DevUser) {
  const name = [user.first_name, user.last_name].filter(Boolean).join(' ');

  return name ? `${name} (${user.email})` : user.email;
}

function DevUserSwitcher() {
  const { user, isSuperUser, signInDev, signOut } = useAuth();
  const [users, setUsers] = useState<DevUser[]>([]);
  const [superUser, setSuperUser] = useState(isSuperUser);

  useEffect(() => {
    void api.dev.users.get().then(({ data }) => {
      if (data) setUsers(data);
    });
  }, []);

  return (
    <Group
      gap="sm"
      px="sm"
      h={DEV_SWITCHER_HEIGHT}
      bg="yellow.1"
      c="yellow.9"
      wrap="nowrap"
      pos="sticky"
      top={0}
      style={{ zIndex: getDefaultZIndex('app') }}
    >
      <Badge color="yellow" variant="filled">
        dev auth
      </Badge>
      <Select
        size="xs"
        w={320}
        placeholder="select user…"
        value={user?.id ?? null}
        data={users.map((u) => ({ value: u.id, label: displayName(u) }))}
        onChange={(value) => {
          if (value) void signInDev(value, superUser);
        }}
      />
      <Switch
        size="xs"
        label="super user"
        checked={superUser}
        onChange={(event) => {
          const next = event.currentTarget.checked;

          setSuperUser(next);

          if (user) void signInDev(user.id, next);
        }}
      />
      {user && (
        <Anchor component="button" type="button" size="xs" onClick={signOut}>
          sign out
        </Anchor>
      )}
    </Group>
  );
}

export function DevAuthFrame({ children }: { children: ReactNode }) {
  return (
    <Box __vars={{ [DEV_SWITCHER_HEIGHT_VAR]: rem(DEV_SWITCHER_HEIGHT) }}>
      <DevUserSwitcher />
      {children}
    </Box>
  );
}
